import * as React from "react";
import { MultiSelect, Loader } from "@mantine/core";
import { useAuth } from "@bi-studio/shared/hooks";
import { getRoles } from "@bi-studio/shared/apis";
import type { Role } from "@bi-studio/shared/apis";

interface RoleSelectorProps {
    value?: string[];
    onChange: (value: string[]) => void;
    label?: string;
    description?: string;
    placeholder?: string;
    error?: string;
    disabled?: boolean;
    required?: boolean;
}

export function RoleSelector({
    value = [],
    onChange,
    label = "Allowed Roles",
    description = "Select roles that can access this resource. Leave empty for public access.",
    placeholder = "Select roles",
    error,
    disabled,
    required,
}: RoleSelectorProps) { // No changes needed to default props, assuming public access comment is accurate usage for now
    const { token, user } = useAuth();
    const [roles, setRoles] = React.useState<Role[]>([]);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        if (!token) return;

        setLoading(true);
        // Fetch roles for the user's company
        getRoles(token, user?.companyId)
            .then((data) => {
                setRoles(data);
            })
            .catch((err) => {
                console.error("Failed to load roles", err);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [token, user?.companyId]);

    const selectData = roles.map((role) => ({
        value: role.id,
        label: role.name,
    }));

    return (
        <MultiSelect
            label={label}
            description={description}
            placeholder={loading ? "Loading roles..." : placeholder}
            data={selectData}
            value={value}
            onChange={onChange}
            disabled={disabled || loading}
            error={error}
            required={required}
            searchable
            nothingFoundMessage="No roles found"
            rightSection={loading ? <Loader size="xs" /> : null}
            clearable
        />
    );
}
